import {
  CategorySortOption,
  enumCategorySortOption,
  enumItemGroupSortOption,
  enumItemSortOption,
  ItemGroupSortOption,
  ItemSortOption,
} from '@rematter/frontend-lib-api-client';
import { DataGridSelection } from '@rematter/ui-web';
import { atom } from 'jotai';

import { categoryDeleteAtom } from '../_graphql/mutationCategoryDelete';
import { itemAliasDeleteAtom } from '../_graphql/mutationItemAliasDelete';
import { itemDeleteAtom } from '../_graphql/mutationItemDelete';
import { itemGroupDeleteAtom } from '../_graphql/mutationItemGroupDelete';
import {
  categoriesListQueryAtom,
  CategoryRecord,
} from '../_graphql/queryCategoriesList';
import { itemAliasesListQueryAtom } from '../_graphql/queryItemAliasesList';
import { itemGroupsListQueryAtom } from '../_graphql/queryItemGroupsList';
import { itemsListQueryAtom } from '../_graphql/queryItemsList';
import {
  enumItemAliasSortOption,
  InventorySection,
  ItemAliasRecord,
  ItemAliasSortOption,
  ItemAliasType,
  ItemGroupRecord,
  ItemRecord,
} from '../types/inventoryItem';

import { atomFromQueryString } from '~/shared/state';

// 🟣🟣🟣 PARAMS
export const $currentSection = atomFromQueryString.string(
  'section',
) as unknown as ReturnType<typeof atom<InventorySection | null>>;

export const $searchTerm = atomFromQueryString.string('q');
export const $limit = atomFromQueryString.number('limit', 25);
export const $offset = atomFromQueryString.number('offset', 0);

// ═══════════════════════════════════════════════════════════════════════════════
// ITEMS
// ═══════════════════════════════════════════════════════════════════════════════

// 🔘🔘🔘 PLAIN ATOMS -> Items
export const $itemsSelectedKeys = atom<DataGridSelection>(new Set<React.Key>());
export const $isItemCreateDialogOpen = atom<boolean>(false);
export const $selectedItem = atom<ItemRecord | null>(null);
export const $itemsSorting = atom<ItemSortOption[]>([
  enumItemSortOption.NameAsc,
]);

// 🟢🟢🟢 REMOTE DATA -> Items
export const $remoteItems = itemsListQueryAtom((get) => {
  const searchTerm = get($searchTerm);
  const limit = get($limit);
  const offset = get($offset);
  const sorting = get($itemsSorting);

  return {
    variables: {
      searchTerm: searchTerm || undefined,
      limit,
      offset,
      sorting,
    },
  };
});

// 🟠🟠🟠 ACTION -> DELETE item
export const $deleteItem = itemDeleteAtom({
  onSuccess: (get) => {
    get($remoteItems).refetch?.();
  },
});

// ═══════════════════════════════════════════════════════════════════════════════
// ITEM ALIASES
// ═══════════════════════════════════════════════════════════════════════════════

// 🔘🔘🔘 PLAIN ATOMS -> Item Aliases
export const $aliasesSelectedKeys = atom<DataGridSelection>(
  new Set<React.Key>(),
);
export const $isAliasCreateDialogOpen = atom<boolean>(false);
export const $selectedAlias = atom<ItemAliasRecord | null>(null);
export const $aliasesSorting = atom<ItemAliasSortOption[]>([
  enumItemAliasSortOption.AliasNameAsc,
]);
export const $selectedAliasTypes =
  atomFromQueryString.stringSet<ItemAliasType>('aliasTypes');

// 🟢🟢🟢 REMOTE DATA -> Item Aliases
export const $remoteItemAliases = itemAliasesListQueryAtom((get) => {
  const searchTerm = get($searchTerm);
  const limit = get($limit);
  const offset = get($offset);
  const sorting = get($aliasesSorting);
  const aliasTypes = Array.from(get($selectedAliasTypes));

  return {
    variables: {
      searchTerm: searchTerm || undefined,
      aliasTypes: aliasTypes.length > 0 ? aliasTypes : undefined,
      limit,
      offset,
      sorting,
    },
  };
});

// 🟠🟠🟠 ACTION -> DELETE item alias
export const $deleteItemAlias = itemAliasDeleteAtom({
  onSuccess: (get) => {
    get($remoteItemAliases).refetch?.();
  },
});

// ═══════════════════════════════════════════════════════════════════════════════
// CATEGORIES
// ═══════════════════════════════════════════════════════════════════════════════

// 🔘🔘🔘 PLAIN ATOMS -> Categories
export const $categoriesSelectedKeys = atom<DataGridSelection>(
  new Set<React.Key>(),
);
export const $isCategoryCreateDialogOpen = atom<boolean>(false);
export const $selectedCategory = atom<CategoryRecord | null>(null);
export const $categoriesSorting = atom<CategorySortOption[]>([
  enumCategorySortOption.NameAsc,
]);

// 🟢🟢🟢 REMOTE DATA -> Categories
export const $remoteCategories = categoriesListQueryAtom((get) => {
  const searchTerm = get($searchTerm);
  const limit = get($limit);
  const offset = get($offset);
  const sorting = get($categoriesSorting);

  return {
    variables: {
      searchTerm: searchTerm || undefined,
      limit,
      offset,
      sorting,
    },
  };
});

// 🟠🟠🟠 ACTION -> DELETE category
export const $deleteCategory = categoryDeleteAtom({
  onSuccess: (get) => {
    get($remoteCategories).refetch?.();
  },
});

// ═══════════════════════════════════════════════════════════════════════════════
// ITEM GROUPS
// ═══════════════════════════════════════════════════════════════════════════════

// 🔘🔘🔘 PLAIN ATOMS -> Item Groups
export const $groupsSelectedKeys = atom<DataGridSelection>(
  new Set<React.Key>(),
);
export const $isGroupCreateDialogOpen = atom<boolean>(false);
export const $selectedGroup = atom<ItemGroupRecord | null>(null);
export const $groupsSorting = atom<ItemGroupSortOption[]>([
  enumItemGroupSortOption.NameAsc,
]);

// 🟢🟢🟢 REMOTE DATA -> Item Groups
export const $remoteItemGroups = itemGroupsListQueryAtom((get) => {
  const searchTerm = get($searchTerm);
  const limit = get($limit);
  const offset = get($offset);
  const sorting = get($groupsSorting);

  return {
    variables: {
      searchTerm: searchTerm || undefined,
      limit,
      offset,
      sorting,
    },
  };
});

// 🟠🟠🟠 ACTION -> DELETE item group
export const $deleteItemGroup = itemGroupDeleteAtom({
  onSuccess: (get) => {
    get($remoteItemGroups).refetch?.();
  },
});
